// Reanudar una tanda del visor que quedó en vuelo — ADR-2026-09-16-las-tandas-en-vuelo-se-guardan-y-se-reanudan.
//
// Carrusel y Animar no tienen `execution_id` que el front conozca: la tanda se guarda
// apenas se pide, con `pendiente`, y cuando el chat vuelve a abrirse (o mientras está
// abierto, si la promesa se perdió con la pantalla) se busca lo que salió en
// `imagenes_generadas` por lo que las filas SÍ guardan: el carrusel lleva
// `datos_generacion.imagen_origen_id`, y el video lleva la imagen de origen como
// `start_frame` dentro de `scenes` (guardado como texto). Se acota por empresa, por fecha
// (desde el pedido, con un margen) y sólo filas con archivo, que son las que se pueden
// dibujar. Lo que ya está en otra tanda del lienzo no se vuelve a tomar: dos carruseles
// de la misma foto no se roban las fotos entre sí.

import { USE_ATLAS_BACKEND } from '../../config';
import { atlasBackendFetch } from '../../lib/atlasBackend';
import { supabase } from '../../lib/supabase';
import type { TandaGuardada } from './persistenciaDelLienzo';

/** Cuánto se espera a una tanda en vuelo antes de darla por perdida. */
export const EDAD_MAXIMA_MS = 30 * 60_000;
/** Margen hacia atrás sobre `creadaEn`: los relojes del cliente y la base no coinciden. */
const MARGEN_MS = 60_000;
/** Cada cuánto se vuelve a mirar mientras el chat está abierto. */
export const INTERVALO_MS = 8_000;
/**
 * Cuánto se le da a un pedido con `execution_id` para que aparezca en el backend antes de
 * preguntarle si lo recibió: la visión que escribe el prompt tarda hasta ~40 s y el
 * pedido sale después (ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego).
 */
export const GRACIA_SIN_PEDIDO_MS = 90_000;
/** Cada cuánto, como mucho, se le pregunta al backend por la misma tanda. */
export const INTERVALO_ESTADO_MS = 60_000;

export const tandaVencida = (t: TandaGuardada, ahora: number): boolean =>
  ahora - t.creadaEn > EDAD_MAXIMA_MS;

/** Los ids que le tocan a la tanda: los primeros `total`, sin los que ya son de otra. */
export function elegirResultados(
  candidatos: readonly string[],
  usados: ReadonlySet<string>,
  total: number,
): string[] {
  const out: string[] = [];
  for (const id of candidatos) {
    if (usados.has(id) || out.includes(id)) continue;
    out.push(id);
    if (out.length >= total) break;
  }
  return out;
}

/** Ids ya colgados en cualquier tanda del lienzo, salvo la que se está resolviendo. */
export function idsUsados(tandas: readonly TandaGuardada[], salvo: string): Set<string> {
  const out = new Set<string>();
  for (const t of tandas) if (t.id !== salvo) for (const id of t.imageIds) out.add(id);
  return out;
}

export type ConsultaDeTanda =
  | { por: 'execution'; executionIds: string[]; total: number }
  | { por: 'origen'; origenId: string; esVideo: boolean; total: number };

/**
 * Cómo se buscan los resultados de una tanda pendiente. Con `execution_id` (las tandas
 * del modo Diseño, ADR-2026-09-17-el-diseno-vive-en-el-jsonb-del-lienzo) la búsqueda es
 * exacta: el backend estampa ese id en cada fila —y la consulta se queda sólo con las
 * finales (`active = true`), porque el mismo `execution_id` lleva también las filas
 * intermedias. Una tanda de VARIOS pedidos (Mostrar en uso, un pedido por foto) trae
 * `executionIds` y se busca por todos. Sin nada de eso, por la imagen de origen, como
 * Carrusel y Animar. Puro, para poder probarlo sin red.
 */
export function consultaDeTanda(t: TandaGuardada): ConsultaDeTanda | null {
  if (!t.pendiente) return null;
  const ids = t.pendiente.executionIds?.length
    ? t.pendiente.executionIds
    : t.pendiente.executionId
      ? [t.pendiente.executionId]
      : [];
  if (ids.length > 0) return { por: 'execution', executionIds: ids, total: t.pendiente.total };
  if (!t.origenId) return null;
  return { por: 'origen', origenId: t.origenId, esVideo: t.esVideo, total: t.pendiente.total };
}

export interface ResultadosDeTanda {
  /** Ids con archivo, en orden de creación. */
  ids: string[];
  /** Filas que el backend escribió como fallidas (`has_error`): ocupan lugar en el total. */
  fallidas: number;
}

const SIN_RESULTADOS: ResultadosDeTanda = { ids: [], fallidas: 0 };

/** Candidatos en la base, en orden de creación. Con error, vacío: se reintenta después. */
export async function buscarResultadosDeTanda(
  companyId: string,
  t: TandaGuardada,
): Promise<ResultadosDeTanda> {
  const consulta = consultaDeTanda(t);
  if (!consulta) return SIN_RESULTADOS;
  const desde = new Date(t.creadaEn - MARGEN_MS).toISOString();
  if (consulta.por === 'execution') {
    // Sólo filas "settled": finales (`active = true`) o FALLIDAS (`has_error = true`),
    // como `pollGeneratedByExecutionId`. El backend escribe filas INTERMEDIAS con `active`
    // en false, y sin este filtro la tanda se quedaba con un paso del medio en vez del
    // resultado. Las fallidas cuentan para saber que la tanda terminó, no se muestran.
    const { data, error } = await supabase
      .from('imagenes_generadas')
      .select('id, has_error')
      .eq('id_company', companyId)
      .in('execution_id', consulta.executionIds)
      .or('active.eq.true,has_error.eq.true')
      .gte('created_at', desde)
      .order('created_at', { ascending: true })
      .limit(consulta.total * 3);
    if (error) return SIN_RESULTADOS;
    const filas = (data ?? []) as { id: string | number; has_error: boolean | null }[];
    return {
      ids: filas.filter((r) => !r.has_error).map((r) => String(r.id)),
      fallidas: filas.filter((r) => r.has_error).length,
    };
  }
  let q = supabase
    .from('imagenes_generadas')
    .select('id')
    .eq('id_company', companyId)
    .not('file_name', 'is', null)
    .gte('created_at', desde)
    .order('created_at', { ascending: true })
    // De más, por si otra tanda del mismo origen ya se quedó con las primeras.
    .limit(consulta.total * 3);
  q = consulta.esVideo
    ? q.eq('tipo', 'video').like('datos_generacion->>scenes', `%${consulta.origenId}%`)
    : q.eq('datos_generacion->>imagen_origen_id', consulta.origenId);
  const { data, error } = await q;
  if (error) return SIN_RESULTADOS;
  return { ids: ((data ?? []) as { id: string | number }[]).map((r) => String(r.id)), fallidas: 0 };
}

/**
 * Qué sabe el backend de un pedido por su `execution_id`
 * (ADR-2026-09-18-una-tanda-en-vuelo-se-relanza-si-el-pedido-no-llego): `en_vuelo` si lo
 * está corriendo (o ya terminó y las filas están por aparecer), `no_recibido` si nunca le
 * llegó, `fallido` si lo cerró con error, `desconocido` si no se pudo preguntar.
 */
export type EstadoDelPedido = 'en_vuelo' | 'no_recibido' | 'fallido' | 'desconocido';

/** Los estados de cada pedido de la tanda, en uno solo. Puro. */
export function resumirEstados(estados: readonly string[]): EstadoDelPedido {
  if (estados.length === 0) return 'desconocido';
  if (estados.some((e) => e === 'queued' || e === 'running' || e === 'succeeded' || e === 'partial')) return 'en_vuelo';
  if (estados.every((e) => e === 'not_found')) return 'no_recibido';
  if (estados.every((e) => e === 'not_found' || e === 'failed')) return 'fallido';
  return 'desconocido';
}

/** Pregunta por cada pedido de la tanda (`POST /v1/ads/executions/status`, el mismo que usa la primera generación). */
export async function consultarEstadoDelPedido(executionIds: readonly string[]): Promise<EstadoDelPedido> {
  if (!USE_ATLAS_BACKEND || executionIds.length === 0) return 'desconocido';
  try {
    const estados = await Promise.all(
      executionIds.map(async (id) => {
        const res = await atlasBackendFetch('/v1/ads/executions/status', { execution_id: id });
        const info = (await res.json()) as { status?: unknown };
        return typeof info.status === 'string' ? info.status : 'error';
      }),
    );
    return resumirEstados(estados);
  } catch {
    return 'desconocido';
  }
}

/** Hay que preguntarle al backend: se busca por `execution_id`, no apareció nada y ya pasó la gracia. */
export function necesitaEstadoDelPedido(t: TandaGuardada, r: ResultadosDeTanda, ahora: number): boolean {
  const consulta = consultaDeTanda(t);
  return (
    consulta?.por === 'execution' && r.ids.length === 0 && r.fallidas === 0 && ahora - t.creadaEn > GRACIA_SIN_PEDIDO_MS
  );
}

export type Reanudacion = 'esperar' | 'resolver' | 'relanzar' | 'quitar';

/**
 * Qué hacer con una tanda pendiente, mirando lo que hay en la base (`ids` ya elegidos,
 * `fallidas`), lo que dijo el backend del pedido (si se le preguntó) y la edad. Puro.
 * - Completa (o terminada, contando las fallidas): se resuelve con lo que salió; si no
 *   salió nada, se va.
 * - El backend nunca recibió el pedido: se relanza si la tanda guardó cómo armarlo, y si
 *   no se va (no se cobró nada). Falló en el backend: se va.
 * - Sigue corriendo en el backend: se espera, aunque haya vencido.
 * - Vencida sin noticias: lo que haya, o nada.
 */
export function decidirReanudacion(
  t: TandaGuardada,
  r: ResultadosDeTanda,
  estado: EstadoDelPedido | null,
  ahora: number,
): Reanudacion {
  if (!t.pendiente) return 'esperar';
  const total = t.pendiente.total;
  if (r.ids.length >= total) return 'resolver';
  if (r.ids.length + r.fallidas >= total) return r.ids.length > 0 ? 'resolver' : 'quitar';
  if (estado === 'no_recibido') return t.pendiente.pedido ? 'relanzar' : 'quitar';
  if (estado === 'fallido') return r.ids.length > 0 ? 'resolver' : 'quitar';
  if (estado === 'en_vuelo') return 'esperar';
  if (tandaVencida(t, ahora)) return r.ids.length > 0 ? 'resolver' : 'quitar';
  return 'esperar';
}
